/**
 * ATS Settings Tab Component
 * Handles API key, SSO secret, and general settings configuration
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { toast } from 'sonner';
import {
    Key,
    Shield,
    Copy,
    RefreshCw,
    Loader2,
    CheckCircle,
    AlertTriangle,
    Eye,
    EyeOff,
    Code
} from 'lucide-react';

const ATSSettingsTab = ({ 
    api, 
    settings, 
    setSettings, 
    onFetchDocs 
}) => {
    const [saving, setSaving] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [showSsoSecret, setShowSsoSecret] = useState(false);
    const [newApiKey, setNewApiKey] = useState(null);
    const [newSsoSecret, setNewSsoSecret] = useState(null);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put('/ats/settings', {
                enabled: settings.enabled,
                default_role: settings.default_role,
                auto_create_users: settings.auto_create_users,
                sync_deactivation: settings.sync_deactivation,
                allowed_domains: settings.allowed_domains
            });
            toast.success('ATS settings saved');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const generateApiKey = async () => {
        try {
            const response = await api.post('/ats/settings/generate-api-key');
            setNewApiKey(response.data.api_key);
            setSettings(prev => ({ 
                ...prev, 
                api_key_set: true, 
                api_key_preview: response.data.api_key.substring(0, 12) + '...' 
            }));
            toast.success('New API key generated');
        } catch (error) {
            toast.error('Failed to generate API key');
        }
    };

    const generateSsoSecret = async () => {
        try {
            const response = await api.post('/ats/settings/generate-sso-secret');
            setNewSsoSecret(response.data.sso_secret);
            setSettings(prev => ({ ...prev, sso_secret_set: true }));
            toast.success('New SSO secret generated');
        } catch (error) {
            toast.error('Failed to generate SSO secret');
        }
    };

    return (
        <div className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                    <Label className="text-base font-semibold">Enable ATS Integration</Label>
                    <p className="text-sm text-muted-foreground">
                        Allow users to login via ATS magic links and SSO
                    </p>
                </div>
                <Switch
                    checked={settings?.enabled || false}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                    data-testid="ats-enable-toggle"
                />
            </div>

            {/* API Key */}
            <div className="p-4 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <Label className="text-base font-semibold flex items-center gap-2">
                            <Key className="h-4 w-4" />
                            API Key
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            For ATS server-to-server API calls
                        </p>
                    </div>
                    {settings?.api_key_set ? (
                        <Badge variant="outline" className="text-emerald-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Configured
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-amber-600">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Not Set
                        </Badge>
                    )}
                </div>
                
                {newApiKey && (
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                        <p className="text-xs text-emerald-600 font-semibold mb-2">
                            New API Key (copy now - will not be shown again):
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 p-2 rounded bg-background text-sm font-mono break-all">
                                {showApiKey ? newApiKey : '•'.repeat(40)}
                            </code>
                            <Button size="icon" variant="ghost" onClick={() => setShowApiKey(!showApiKey)}>
                                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => copyToClipboard(newApiKey)}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
                
                {settings?.api_key_preview && !newApiKey && (
                    <p className="text-sm text-muted-foreground">
                        Current key: <code className="px-1 py-0.5 rounded bg-muted">{settings.api_key_preview}</code>
                    </p>
                )}
                
                <Button variant="outline" onClick={generateApiKey} className="w-full" data-testid="ats-generate-api-key-btn">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {settings?.api_key_set ? 'Regenerate API Key' : 'Generate API Key'}
                </Button>
            </div>

            {/* SSO Secret */}
            <div className="p-4 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <Label className="text-base font-semibold flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            SSO Secret
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            For signing SSO tokens from your ATS
                        </p>
                    </div>
                    {settings?.sso_secret_set ? (
                        <Badge variant="outline" className="text-emerald-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Configured
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-amber-600">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Not Set
                        </Badge>
                    )}
                </div>
                
                {newSsoSecret && (
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                        <p className="text-xs text-emerald-600 font-semibold mb-2">
                            New SSO Secret (share with ATS team securely):
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 p-2 rounded bg-background text-sm font-mono break-all">
                                {showSsoSecret ? newSsoSecret : '•'.repeat(48)}
                            </code>
                            <Button size="icon" variant="ghost" onClick={() => setShowSsoSecret(!showSsoSecret)}>
                                {showSsoSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => copyToClipboard(newSsoSecret)}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
                
                <Button variant="outline" onClick={generateSsoSecret} className="w-full" data-testid="ats-generate-sso-secret-btn">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {settings?.sso_secret_set ? 'Regenerate SSO Secret' : 'Generate SSO Secret'}
                </Button>
            </div>

            {/* Other Settings */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Label>Auto-create users from ATS</Label>
                        <p className="text-xs text-muted-foreground">
                            Automatically create users when they login via ATS
                        </p>
                    </div>
                    <Switch
                        checked={settings?.auto_create_users ?? true}
                        onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_create_users: checked }))}
                    />
                </div>
                
                <div className="flex items-center justify-between">
                    <div>
                        <Label>Sync user deactivation</Label>
                        <p className="text-xs text-muted-foreground">
                            Deactivate users not in ATS sync list
                        </p>
                    </div>
                    <Switch
                        checked={settings?.sync_deactivation ?? true}
                        onCheckedChange={(checked) => setSettings(prev => ({ ...prev, sync_deactivation: checked }))}
                    />
                </div>
                
                <div>
                    <Label>Default role for ATS users</Label>
                    <select
                        className="mt-1 w-full p-2 border rounded-md bg-background"
                        value={settings?.default_role || 'user'}
                        onChange={(e) => setSettings(prev => ({ ...prev, default_role: e.target.value }))}
                    >
                        <option value="user">User (Learner)</option>
                        <option value="trainer">Trainer</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
            </div>

            <Separator />

            <div className="flex gap-3">
                <Button onClick={saveSettings} disabled={saving} className="flex-1" data-testid="ats-save-settings-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Save Settings
                </Button>
                <Button variant="outline" onClick={onFetchDocs} data-testid="ats-integration-docs-btn">
                    <Code className="h-4 w-4 mr-2" />
                    Integration Docs
                </Button>
            </div>
        </div>
    );
};

export default ATSSettingsTab;
